import { useState } from "react";
import {
  DataTable,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { TitrationChart } from "@/components/tools/chem-reactions/titration-chart";
import { t } from "@/lib/i18n/interpolate";
import type { SoluteKind } from "@/lib/tool-kit/chem-reactions/acid-base";
import {
  INDICATORS,
  WEAK_ACIDS,
  WEAK_BASES,
  type WeakConstant,
} from "@/lib/tool-kit/chem-reactions/acid-base-data";
import {
  type CurveInput,
  curveEquivalence,
  indicatorFits,
  isWeak,
  keyPoints,
  steepRange,
  titrationCurve,
} from "@/lib/tool-kit/chem-reactions/titration";
import {
  formatFixed,
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/chemistry/format";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings for the curve panel. The titration widget spreads these in. */
export const TITRATION_CURVE_STRINGS = {
  kindLabel: "What is in the flask?",
  kindStrongAcid: "A strong acid (HCl, HNO₃), titrated with a strong base",
  kindWeakAcid: "A weak acid, titrated with a strong base",
  kindStrongBase: "A strong base (NaOH, KOH), titrated with a strong acid",
  kindWeakBase: "A weak base, titrated with a strong acid",
  presetAcidLabel: "Weak acid",
  presetBaseLabel: "Weak base",
  presetCustom: "Enter my own constant",
  kaLabel: "Acid constant, Ka",
  kbLabel: "Base constant, Kb",
  kHint: "At 25 °C. Write it as 1.8e-5 or 1.8 × 10^-5.",
  concentrationLabel: "Concentration in the flask (mol/L)",
  volumeLabel: "Volume in the flask (mL)",
  titrantLabel: "Titrant concentration (mol/L)",
  badNumbers:
    "Enter positive numbers for both concentrations, the volume and the constant.",
  acidNames: {
    acetic: "Acetic acid",
    formic: "Formic acid",
    hydrofluoric: "Hydrofluoric acid",
    nitrous: "Nitrous acid",
    hypochlorous: "Hypochlorous acid",
    hydrocyanic: "Hydrocyanic acid",
  },
  baseNames: {
    ammonia: "Ammonia",
    methylamine: "Methylamine",
    dimethylamine: "Dimethylamine",
    trimethylamine: "Trimethylamine",
    aniline: "Aniline",
  },
  equivalenceVolume: "Equivalence volume",
  equivalencePh: "pH at equivalence",
  startPh: "pH at the start",
  chartLabel:
    "Titration curve: pH against the volume of titrant added, with the equivalence point marked.",
  chartX: "Titrant added (mL)",
  chartY: "pH",
  chartLegend:
    "Filled dot: equivalence point. Open dot: half-equivalence point, where pH = pKa for a weak acid.",
  keyHeading: "Key points",
  columnPoint: "Point",
  columnVolume: "Titrant (mL)",
  columnPh: "pH",
  pointStart: "Start",
  pointHalf: "Half-equivalence",
  pointEquivalence: "Equivalence",
  pointBeyond: "1.5 × equivalence",
  indicatorHeading: "Choosing an indicator",
  indicatorNote:
    "An indicator suits this titration when the middle of its colour-change range lies in the steep part of the curve, which here runs from pH {low} to pH {high} within ±0.5 % of the equivalence volume.",
  columnIndicator: "Indicator",
  columnRange: "Colour changes at pH",
  columnSuits: "Suitable?",
  yes: "Yes",
  no: "No",
  indicators: {
    bromocresolGreen: "Bromocresol green",
    methylRed: "Methyl red",
    bromothymolBlue: "Bromothymol blue",
    phenolRed: "Phenol red",
    phenolphthalein: "Phenolphthalein",
    thymolphthalein: "Thymolphthalein",
  },
  weakNote:
    "Because the analyte is weak, the pH at equivalence is not 7: the salt that forms reacts with water. The buffer region around half-equivalence is where pH ≈ pKa.",
  strongNote:
    "Strong acid with strong base (or the reverse) gives pH 7.00 at equivalence at 25 °C, because neither ion reacts with water.",
} as const;

type CurveStrings = typeof TITRATION_CURVE_STRINGS;

const DEFAULT_CONSTANT = "1.8e-5";
const PH_PLACES = 2;
const VOLUME_SIG = 4;

function presetsFor(kind: SoluteKind): readonly WeakConstant[] {
  return kind === "weak-base" ? WEAK_BASES : WEAK_ACIDS;
}

function pointLabel(id: string, strings: CurveStrings): string {
  const labels: Record<string, string> = {
    start: strings.pointStart,
    half: strings.pointHalf,
    equivalence: strings.pointEquivalence,
    beyond: strings.pointBeyond,
  };
  return labels[id] ?? id;
}

function CurveResults({
  input,
  strings,
  fmt,
}: {
  input: CurveInput;
  strings: CurveStrings;
  fmt: NumberFormatter;
}) {
  const points = keyPoints(input);
  const equivalence = points.find((p) => p.id === "equivalence") ?? points[0];
  const start = points[0];
  const half = points.find((p) => p.id === "half") ?? null;
  const weak = isWeak(input.solute);
  const steep = steepRange(input);
  const ph = (value: number) => formatFixed(fmt, value, PH_PLACES);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.equivalenceVolume}
          value={`${formatSig(fmt, curveEquivalence(input), VOLUME_SIG)} mL`}
        />
        <StatCard label={strings.equivalencePh} value={ph(equivalence.pH)} />
        <StatCard label={strings.startPh} value={ph(start.pH)} />
      </StatGrid>
      <TitrationChart
        equivalence={equivalence}
        format={(value) => fmt(value, { maximumFractionDigits: 2 })}
        half={weak ? half : null}
        label={strings.chartLabel}
        points={titrationCurve(input)}
        xLabel={strings.chartX}
        yLabel={strings.chartY}
      />
      <p className="text-muted-foreground text-xs">{strings.chartLegend}</p>
      <Notice>{weak ? strings.weakNote : strings.strongNote}</Notice>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.keyHeading}</h3>
        <DataTable
          head={[strings.columnPoint, strings.columnVolume, strings.columnPh]}
          rows={points.map((p) => [
            pointLabel(p.id, strings),
            formatFixed(fmt, p.volume, PH_PLACES),
            ph(p.pH),
          ])}
        />
      </div>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.indicatorHeading}</h3>
        <p className="text-muted-foreground text-sm">
          {t(strings.indicatorNote, {
            low: ph(steep.low),
            high: ph(steep.high),
          })}
        </p>
        <DataTable
          head={[
            strings.columnIndicator,
            strings.columnRange,
            strings.columnSuits,
          ]}
          rows={indicatorFits(input).map((fit) => {
            const found = INDICATORS.find((i) => i.id === fit.indicator.id);
            return [
              strings.indicators[
                fit.indicator.id as keyof typeof strings.indicators
              ],
              `${fmt(found?.low ?? 0, { minimumFractionDigits: 1 })} – ${fmt(found?.high ?? 0, { minimumFractionDigits: 1 })}`,
              fit.suitable ? strings.yes : strings.no,
            ];
          })}
        />
      </div>
    </>
  );
}

export function TitrationCurvePanel({
  strings,
  fmt,
}: {
  strings: CurveStrings;
  fmt: NumberFormatter;
}) {
  const [kind, setKind] = useState<SoluteKind>("weak-acid");
  const [preset, setPreset] = useState("acetic");
  const [constant, setConstant] = useState(DEFAULT_CONSTANT);
  const [concentration, setConcentration] = useState("0.100");
  const [volume, setVolume] = useState("25.00");
  const [titrant, setTitrant] = useState("0.100");

  const weak = kind === "weak-acid" || kind === "weak-base";
  const presets = presetsFor(kind);
  const names: Record<string, string> =
    kind === "weak-base" ? strings.baseNames : strings.acidNames;

  function chooseKind(next: SoluteKind) {
    setKind(next);
    const first = presetsFor(next)[0];
    setPreset(first.id);
    setConstant(first.k.toExponential());
  }

  function choosePreset(id: string) {
    setPreset(id);
    const found = presets.find((p) => p.id === id);
    if (found) {
      setConstant(found.k.toExponential());
    }
  }

  const c = parseMeasuredNumber(concentration);
  const v = parseMeasuredNumber(volume);
  const ct = parseMeasuredNumber(titrant);
  const k = parseMeasuredNumber(constant);
  const valid =
    c !== null &&
    c > 0 &&
    v !== null &&
    v > 0 &&
    ct !== null &&
    ct > 0 &&
    (!weak || (k !== null && k > 0));

  return (
    <>
      <SelectField
        id="tc-kind"
        label={strings.kindLabel}
        onChange={chooseKind}
        options={[
          { value: "strong-acid", label: strings.kindStrongAcid },
          { value: "weak-acid", label: strings.kindWeakAcid },
          { value: "strong-base", label: strings.kindStrongBase },
          { value: "weak-base", label: strings.kindWeakBase },
        ]}
        value={kind}
      />
      {weak ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="tc-preset"
            label={
              kind === "weak-base"
                ? strings.presetBaseLabel
                : strings.presetAcidLabel
            }
            onChange={choosePreset}
            options={[
              ...presets.map((p) => ({
                value: p.id,
                label: `${names[p.id]} (${p.formula})`,
              })),
              { value: "custom", label: strings.presetCustom },
            ]}
            value={preset}
          />
          <TextField
            hint={strings.kHint}
            id="tc-constant"
            label={kind === "weak-base" ? strings.kbLabel : strings.kaLabel}
            numeric
            onChange={(value) => {
              setConstant(value);
              setPreset("custom");
            }}
            value={constant}
          />
        </div>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          id="tc-concentration"
          label={strings.concentrationLabel}
          numeric
          onChange={setConcentration}
          value={concentration}
        />
        <TextField
          id="tc-volume"
          label={strings.volumeLabel}
          numeric
          onChange={setVolume}
          value={volume}
        />
        <TextField
          id="tc-titrant"
          label={strings.titrantLabel}
          numeric
          onChange={setTitrant}
          value={titrant}
        />
      </div>
      {valid ? (
        <CurveResults
          fmt={fmt}
          input={{
            solute: { kind, k: weak ? (k as number) : undefined },
            analyteConcentration: c as number,
            analyteVolume: v as number,
            titrantConcentration: ct as number,
          }}
          strings={strings}
        />
      ) : (
        <ErrorNotice>{strings.badNumbers}</ErrorNotice>
      )}
    </>
  );
}
